import { useFlag as useUnleashFlag } from '@unleash/proxy-client-react';
import { useFeatureFlagContext } from '../providers/FeatureFlagProvider';
import { KNOWN_FLAG_META } from '../types/feature-flags.generated';
import type { FlagName } from '../types/feature-flags.generated';

/**
 * Returns the feature flag system status: where flags are sourced from
 * and whether the client has finished loading.
 */
export function useFeatureFlags() {
  return useFeatureFlagContext();
}

/**
 * Returns whether a named flag is enabled for the current user/context.
 *
 * - In bypass mode: always returns true (all flags on).
 * - On Unleash error (unleash-failed): applies the type-aware outage matrix
 *   over the manifest-baked KNOWN_FLAG_META (task 340):
 *     - permission   → false (fail closed)
 *     - kill-switch / operational / release / experiment → the flag's baked
 *       `enabled` (kill-switches fall back to their baked value too: keep-running
 *       levers are baked on, inverted levers like metering bypass are baked off
 *       and must stay off during an outage)
 *     - name missing from the meta → false (fail closed)
 * - Otherwise: delegates to @unleash/proxy-client-react useFlag().
 */
export function useFlag(name: FlagName): boolean {
  const { source } = useFeatureFlagContext();

  // useUnleashFlag must always be called (React rules of hooks).
  // In bypass or error modes, its return value is overridden below.
  const unleashValue = useUnleashFlag(name);

  if (source === 'bypass') return true;
  if (source === 'unleash-failed') {
    // Typed as possibly-undefined: callers can only pass FlagName, but the
    // meta is baked at build time and must fail closed on any drift.
    const meta: { type: string; enabled: boolean } | undefined = KNOWN_FLAG_META[name];
    if (!meta) return false;
    if (meta.type === 'permission') return false;
    return meta.enabled;
  }
  return unleashValue;
}
